import {
  type FunctionComponent,
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Job, Payment, actions } from '@kanda-libs/ks-frontend-services';

import { useAppDispatch } from 'components/App';
import useApiError from 'hooks/useApiError';
import { useStatusContext } from 'pages/Status/context';

import { SUBHEADING_ARRAY } from './constants';

export type DepositDisplay = 'loading' | 'card' | 'bank' | 'paid' | 'error';

export interface StatusDepositContextType {
  display: DepositDisplay;
  job: Job | undefined;
  deposit: Payment | undefined;
  togglePaymentMethod: (
    nextDisplay: DepositDisplay,
    successCallback?: () => void,
  ) => void;
  isSubmitting: boolean;
  subheading: string[];
}

export const StatusDepositContext =
  createContext<StatusDepositContextType | null>(null);

export const useStatusDepositContext = () =>
  useContext(StatusDepositContext) as StatusDepositContextType;

export interface StatusDepositProviderProps {
  children: ReactNode;
}

const StatusDepositProvider: FunctionComponent<StatusDepositProviderProps> =
  function ({ children }) {
    const [display, setDisplay] = useState<DepositDisplay>('loading');
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const { id, job, deposit, jobCompanyInfo } = useStatusContext();
    const dispatch = useAppDispatch();
    const handleApiError = useApiError(
      'Error setting up deposit payment for this job - please refresh and try again, or reach out to Ideal4Finance for help',
    );

    const companyName = useMemo(
      () => jobCompanyInfo?.company_name ?? 'your tradesperson',
      [jobCompanyInfo],
    );

    const subheading = useMemo(
      () =>
        SUBHEADING_ARRAY.map((segment) =>
          segment.replaceAll('$partner', companyName),
        ),
      [companyName],
    );

    const togglePaymentMethod = useCallback(
      (nextDisplay: DepositDisplay, successCallback?: () => void) => {
        if (!id) return;
        setIsSubmitting(true);
        dispatch(
          actions.jobSwitchDeposit({
            params: {
              id,
            },
            onError: (err) => {
              setIsSubmitting(false);
              setDisplay('error');
              handleApiError(err);
            },
            onSuccess: () => {
              if (successCallback) successCallback();
              setIsSubmitting(false);
              setDisplay(nextDisplay);
            },
          }),
        );
      },
      [id, dispatch, handleApiError],
    );

    const values = useMemo(
      () => ({
        display,
        job,
        deposit,
        subheading,
        togglePaymentMethod,
        isSubmitting,
      }),
      [display, job, deposit, subheading, togglePaymentMethod, isSubmitting],
    );

    const initRef = useRef<boolean>(false);
    useEffect(() => {
      if (!deposit || !id || initRef.current) return;
      initRef.current = true;
      if (deposit.status === 'paid') {
        setDisplay('paid');
        return;
      }
      if (deposit.payment_option.payment_method === 'bank') {
        setDisplay('bank');
        return;
      }
      togglePaymentMethod('bank');
    }, [id, togglePaymentMethod, deposit]);

    return (
      <StatusDepositContext.Provider value={values}>
        {children}
      </StatusDepositContext.Provider>
    );
  };

export default StatusDepositProvider;
